import * as React from 'react';
import styled, { css } from 'styled-components';
import { body } from '@lendi-ui/typography';
import { deriveSize } from '@lendi-ui/utils';
import { getRadius, getFocusRing } from '@lendi-ui/commons/borders';
import { mr, ml, mx, mb } from '@lendi-ui/spacing';
import { jsxs, jsx } from 'react/jsx-runtime';

function _toPrimitive(input, hint) {
  if (typeof input !== "object" || input === null) return input;
  var prim = input[Symbol.toPrimitive];
  if (prim !== undefined) {
    var res = prim.call(input, hint || "default");
    if (typeof res !== "object") return res;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return (hint === "string" ? String : Number)(input);
}

function _toPropertyKey(arg) {
  var key = _toPrimitive(arg, "string");
  return typeof key === "symbol" ? key : String(key);
}

function _defineProperty(obj, key, value) {
  key = _toPropertyKey(key);
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}

var CheckboxLabel = styled.p.withConfig({
  displayName: "indexstyle__CheckboxLabel",
  componentId: "lui__sc-j4zsex-0"
})(["", ";font-family:", ";font-weight:", ";display:inline-block;"], props => body(props), _ref => {
  var {
    theme
  } = _ref;
  return theme.asset.font.body.family;
}, _ref2 => {
  var {
    theme
  } = _ref2;
  return theme.asset.font.body.weight;
});
var isLegacyTheme = themeName => {
  return themeName === 'lendi' || themeName === 'domain';
};
var Wrapper$1 = styled.label.withConfig({
  displayName: "indexstyle__Wrapper",
  componentId: "lui__sc-j4zsex-1"
})(["width:100%;display:flex;align-items:center;border-radius:", ";", " ", " ", ";&:hover{", ";}"], _ref3 => {
  var {
    size,
    theme
  } = _ref3;
  if (theme.name === 'domain') {
    return '3px';
  }
  return size === 'xs' ? '20px' : getRadius();
}, _ref4 => {
  var {
    size
  } = _ref4;
  switch (size) {
    case 'xs':
      return css(["height:", ";"], deriveSize(24 / 16));
    case 'sm':
      return css(["height:", ";"], deriveSize(38 / 16));
    case 'lg':
      return css(["height:", ";"], deriveSize(62 / 16));
    case 'md':
    default:
      return css(["height:", ";"], deriveSize(46 / 16));
  }
}, _ref5 => {
  var {
    isBoxed,
    checked,
    theme
  } = _ref5;
  if (isBoxed) {
    if (checked) {
      return css(["border:1px solid ", ";background-color:", ";", ""], theme.color.border.interaction.selected, theme.color.fill.selected, !isLegacyTheme(theme.name) && "&:focus-visible, &:focus-within {\n              box-shadow: ".concat(getFocusRing(), ";\n            }"));
    } else {
      return css(["border:1px solid ", ";", ""], theme.color.border.interaction.default, _ref6 => {
        var {
          theme
        } = _ref6;
        return !isLegacyTheme(theme.name) && "&:focus-visible,\n          &:focus-within {\n            box-shadow: ".concat(getFocusRing(), ";\n          }");
      });
    }
  }
  return null;
}, _ref7 => {
  var {
    disabled
  } = _ref7;
  if (disabled) {
    return css(["cursor:not-allowed;opacity:0.4;"]);
  }
  return css(["cursor:pointer;"]);
}, _ref8 => {
  var {
    isBoxed,
    checked,
    disabled,
    theme
  } = _ref8;
  if (isBoxed && !disabled) {
    if (checked) {
      return css(["border:1px solid ", ";background-color:", ";"], theme.color.interaction.main, isLegacyTheme(theme.name) ? theme.color.fill.main : theme.color.fill.selected);
    } else {
      return css(["border:1px solid ", ";"], theme.color.shade['400']);
    }
  }
  return null;
});
var tickMixin = base => css(["width:", ";height:", ";top:", "px;"], deriveSize(0.2 * base), deriveSize(0.6 * base), base);
var CheckboxWrapper = styled.input.withConfig({
  displayName: "indexstyle__CheckboxWrapper",
  componentId: "lui__sc-j4zsex-2"
})(["flex-shrink:0;cursor:pointer;position:relative;display:inline-block;appearance:none;border-radius:", ";box-sizing:border-box;", " ", " ", " &:hover{", ";}&:focus{", "}&:disabled{cursor:not-allowed;pointer-events:none;}&:checked::after{box-sizing:content-box;content:'';left:0;right:0;margin:auto;position:absolute;border:solid #fff;border-width:0 2px 2px 0;transform:rotate(45deg);", "}", ""], getRadius('checkbox'), _ref9 => {
  var {
    hasNoLeftMargin
  } = _ref9;
  if (hasNoLeftMargin) {
    return css(["", ";", ";"], mr('xxs'), ml('nil'));
  } else {
    return css(["", ";"], mx('xxs'));
  }
}, _ref10 => {
  var {
    checked,
    disabled,
    theme
  } = _ref10;
  if (!disabled) {
    if (checked) {
      return css(["border:0;background-color:", ";"], theme.color.interaction.main);
    }
    return css(["border:2px solid ", ";background-color:", ";"], theme.color.border.interaction.default, theme.color.fill.main);
  } else {
    if (checked) {
      return css(["cursor:not-allowed;pointer-events:none;opacity:0.4;background-color:", ";border:0;"], theme.color.interaction.main);
    }
    return css(["cursor:not-allowed;pointer-events:none;opacity:0.4;border:2px solid ", ";background-color:", ";"], theme.color.border.interaction.default, theme.color.fill.main);
  }
}, _ref11 => {
  var {
    inputSize
  } = _ref11;
  switch (inputSize) {
    case 'xs':
      return css(["width:", ";height:", ";"], deriveSize(18 / 16), deriveSize(18 / 16));
    case 'sm':
      return css(["height:", ";width:", ";"], deriveSize(20 / 16), deriveSize(20 / 16));
    case 'lg':
      return css(["width:", ";height:", ";"], deriveSize(38 / 16), deriveSize(38 / 16));
    case 'md':
    default:
      return css(["height:", ";width:", ";"], deriveSize(32 / 16), deriveSize(32 / 16));
  }
}, _ref12 => {
  var {
    checked,
    disabled,
    theme
  } = _ref12;
  if (!disabled) {
    if (checked) {
      if (theme.name === 'lendi') {
        return css(["background-color:#99e5db;"]);
      }
      if (theme.name === 'domain') {
        return css(["background-color:#9ddc99;"]);
      }
      return null;
    }
    return css(["border:2px solid ", ";"], theme.color.interaction.main);
  }
  return null;
}, _ref13 => {
  var {
    disabled
  } = _ref13;
  return disabled && css(["outline:none;"]);
}, _ref14 => {
  var {
    inputSize
  } = _ref14;
  switch (inputSize) {
    case 'xs':
      return tickMixin(1);
    case 'sm':
      return tickMixin(20 / 16);
    case 'lg':
      return tickMixin(38 / 16);
    case 'md':
    default:
      return tickMixin(32 / 16);
  }
}, _ref15 => {
  var {
    isBoxed,
    theme
  } = _ref15;
  if (!isLegacyTheme(theme.name)) {
    return css(["&:focus-visible{", " outline:0;}"], !isBoxed && "box-shadow: ".concat(getFocusRing(), ";"));
  }
  return null;
});

var _excluded$1 = ["value", "label", "isChecked", "onChange", "isDisabled", "isBoxed", "hasNoLeftMargin", "className", "size"];
var Checkbox = _ref => {
  var {
      value,
      label,
      isChecked = false,
      onChange,
      isDisabled = false,
      isBoxed = false,
      hasNoLeftMargin = false,
      className,
      size = 'md'
    } = _ref,
    otherProps = _objectWithoutProperties(_ref, _excluded$1);
  return /*#__PURE__*/jsxs(Wrapper$1, {
    isBoxed: isBoxed,
    checked: isChecked,
    disabled: isDisabled,
    className: className,
    size: size,
    children: [/*#__PURE__*/jsx(CheckboxWrapper, _objectSpread2({
      type: "checkbox",
      inputSize: size,
      value: value,
      checked: isChecked,
      onChange: onChange,
      disabled: isDisabled,
      isBoxed: isBoxed,
      hasNoLeftMargin: hasNoLeftMargin
    }, otherProps)), typeof label === 'string' && /*#__PURE__*/jsx(CheckboxLabel, {
      size: size,
      color: isDisabled ? 'shade.200' : undefined,
      children: label
    }), typeof label !== 'string' && label]
  });
};

function _objectDestructuringEmpty(obj) {
  if (obj == null) throw new TypeError("Cannot destructure " + obj);
}

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}

var Wrapper = styled.div.withConfig({
  displayName: "indexstyle__Wrapper",
  componentId: "lui__sc-1bnbeca-0"
})(["width:100%;display:flex;flex-direction:", ";"], _ref => {
  var {
    direction
  } = _ref;
  return direction;
});

var _excluded = ["children", "values", "isBoxed", "className", "direction", "isDisabled", "onChange", "size"];
var CustomCheckbox = styled(Checkbox).withConfig({
  displayName: "CheckboxGroup__CustomCheckbox",
  componentId: "lui__sc-fno9rb-0"
})(["", ";"], mb('xxs'));
var CheckboxGroupItem = props => /*#__PURE__*/jsx(CheckboxContext.Consumer, {
  children: state => {
    var otherProps = _extends({}, (_objectDestructuringEmpty(props), props));
    return /*#__PURE__*/jsx(CustomCheckbox, _objectSpread2(_objectSpread2(_objectSpread2({}, state), otherProps), {}, {
      isChecked: state.values.includes(props.value)
    }));
  }
});
var CheckboxContext = /*#__PURE__*/React.createContext({
  values: []
});
class CheckboxGroup extends React.Component {
  render() {
    var _this$props = this.props,
      {
        children,
        values,
        isBoxed = false,
        className,
        direction = 'column',
        isDisabled,
        onChange,
        size = 'md'
      } = _this$props,
      checkboxProps = _objectWithoutProperties(_this$props, _excluded);
    var checkboxGroupWrapperProps = {
      isBoxed,
      values,
      isDisabled,
      onChange,
      size
    };
    return /*#__PURE__*/jsx(CheckboxContext.Provider, {
      value: checkboxGroupWrapperProps,
      children: /*#__PURE__*/jsx(Wrapper, _objectSpread2(_objectSpread2({
        direction: direction,
        className: className
      }, checkboxProps), {}, {
        children: children
      }))
    });
  }
}
_defineProperty(CheckboxGroup, "Checkbox", CheckboxGroupItem);

export { Checkbox, CheckboxGroup };
